// Detalhe de um meio de pagamento: qual white label, qual método, quem processa.
//
// É página própria, e não a gaveta do painel, porque se chega aqui por link — da aba de métodos
// do white label e da lista de vínculos do processador. Um destino que só existe dentro de outra
// tela não pode ser compartilhado nem reaberto.
import {
  ArrowLeftIcon,
  CreditCardIcon,
  ExternalLinkIcon,
  ServerOffIcon,
  ShuffleIcon,
} from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useParams } from "react-router-dom"

import { CertificateStatusBadge } from "@/components/commons/certificate-status-badge"
import { AppBreadcrumb } from "@/components/layout/app-breadcrumb"
import { DashboardContainer } from "@/components/layout/dashboard-container"
import { Header } from "@/components/layout/header"
import {
  InfoItem,
  SectionCard,
} from "@/components/shared/detail-components"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { FRONTEND_ROUTES } from "@/constants"
import {
  ChangeProcessorDialog,
  ConfigEventsTrail,
  type PendingProcessorChange,
  ProcessorSelectCell,
} from "@/features/processor-config"
import {
  useActivateProcessorConfigMutation,
  useDeactivateProcessorConfigMutation,
  useProcessorConfigDetail,
  useProcessorConfigMetadata,
} from "@/services/admin/processor-config"

const prefix = "pages.adminProcessorConfig"

export function PaymentMethodDetailPage() {
  const { t } = useTranslation()
  const { configId = "" } = useParams()

  const {
    data: config,
    isLoading,
    isError,
    refetch,
  } = useProcessorConfigDetail(configId)
  const { data: metadata } = useProcessorConfigMetadata()

  const activate = useActivateProcessorConfigMutation()
  const deactivate = useDeactivateProcessorConfigMutation()

  // Troca aguardando confirmação — o alerta é o mesmo do painel, por decisão:
  // dois textos para a mesma consequência acabariam divergindo.
  const [pendingChange, setPendingChange] =
    useState<PendingProcessorChange | null>(null)

  const gatewayLabel =
    metadata?.find(
      (item) => item.gateway === config?.gateway
    )?.label ??
    config?.gateway ??
    ""

  return (
    <DashboardContainer>
      <Header>
        <AppBreadcrumb
          links={[
            {
              href: FRONTEND_ROUTES.DASHBOARD.href,
              label: t("sidebar.dashboard"),
            },
            { label: t("sidebar.administration") },
            { label: t(`${prefix}.title`) },
            { label: configId },
          ]}
        />
      </Header>

      <section className="flex flex-1 flex-col gap-4 overflow-x-hidden p-4 @container">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold tracking-tight">
              {config
                ? t(`${prefix}.methods.${config.method}`, {
                    defaultValue: config.method,
                  })
                : t(`${prefix}.detail.title`)}
            </h1>
            <p className="text-sm text-muted-foreground">
              {config?.whiteLabelName ??
                config?.whiteLabelId ??
                ""}
              {config ? ` · ${gatewayLabel}` : ""}
            </p>
          </div>
          <Button asChild variant="outline">
            <Link
              to={
                config?.whiteLabelId
                  ? `${FRONTEND_ROUTES.ADMIN_WHITE_LABELS.href}/${config.whiteLabelId}?tab=methods`
                  : FRONTEND_ROUTES.ADMIN_PROCESSOR_CONFIG
                      .href
              }
            >
              <ArrowLeftIcon
                className="mr-2 size-4"
                aria-hidden="true"
              />
              {t(`${prefix}.detail.backToWhiteLabel`)}
            </Link>
          </Button>
        </div>

        {isError ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed p-10 text-center">
            <ServerOffIcon
              className="size-8 text-muted-foreground"
              aria-hidden="true"
            />
            <p className="text-sm text-muted-foreground">
              {t(`${prefix}.loadError`)}
            </p>
            <Button
              variant="outline"
              onClick={() => refetch()}
            >
              {t(`${prefix}.retry`)}
            </Button>
          </div>
        ) : isLoading || !config ? (
          <div
            data-testid="payment-method-detail-skeleton"
            className="space-y-4"
          >
            <Skeleton className="h-28 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <SectionCard
              title={t(`${prefix}.detail.overview`)}
              icon={<CreditCardIcon className="size-4" />}
            >
              <div className="grid gap-4 @2xl:grid-cols-3">
                <InfoItem
                  label={t(`${prefix}.detail.status`)}
                >
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={config.active}
                      aria-label={t(
                        `${prefix}.toggleAria`,
                        {
                          method: config.method,
                        }
                      )}
                      onCheckedChange={(next) =>
                        next
                          ? activate.mutate(config.id)
                          : deactivate.mutate(config.id)
                      }
                    />
                    <Badge
                      variant="outline"
                      className={
                        config.active
                          ? "border-emerald-200 bg-emerald-50 font-normal text-emerald-700"
                          : "border-gray-200 bg-gray-50 font-normal text-gray-700"
                      }
                    >
                      {config.active
                        ? t(`${prefix}.active`)
                        : t(`${prefix}.inactive`)}
                    </Badge>
                  </div>
                </InfoItem>

                <InfoItem
                  label={t(`${prefix}.detail.gateway`)}
                >
                  {gatewayLabel}
                </InfoItem>

                <InfoItem
                  label={t(`${prefix}.detail.certificate`)}
                >
                  {config.certificate ? (
                    <CertificateStatusBadge
                      status={config.certificate.status}
                      daysToExpire={
                        config.certificate.daysToExpire
                      }
                    />
                  ) : (
                    <span className="text-muted-foreground">
                      —
                    </span>
                  )}
                </InfoItem>
              </div>
            </SectionCard>

            {/* As CREDENCIAIS são editadas em Processadores, onde a mudança vale para
                todos os métodos que as usam. Aqui se escolhe QUAL delas atende este
                método — que é decisão deste meio de pagamento, não da credencial. */}
            <SectionCard
              title={t(`${prefix}.detail.processedBy`)}
              icon={<ShuffleIcon className="size-4" />}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="space-y-1">
                  <div data-testid="config-processor">
                    <ProcessorSelectCell
                      item={config}
                      disabled={pendingChange !== null}
                      onSelect={(item, processor) =>
                        setPendingChange({
                          item,
                          processor,
                        })
                      }
                    />
                  </div>
                  {config.fallbackProcessor &&
                  config.fallbackProcessor.id !==
                    config.processor?.id ? (
                    <p className="text-xs text-muted-foreground">
                      {t(
                        `${prefix}.detail.fallbackProcessor`,
                        {
                          name:
                            config.fallbackProcessor.name ??
                            config.fallbackProcessor.id,
                        }
                      )}
                    </p>
                  ) : null}
                  <p className="text-xs text-muted-foreground">
                    {t(`${prefix}.detail.processorHint`)}
                  </p>
                </div>
                <Button
                  asChild
                  variant="outline"
                  disabled={!config.processor}
                >
                  <Link
                    to={
                      config.processor
                        ? FRONTEND_ROUTES.ADMIN_MERCHANT_PROCESSOR_DETAIL.href.replace(
                            ":processorId",
                            config.processor.id
                          )
                        : FRONTEND_ROUTES
                            .ADMIN_MERCHANT_PROCESSORS.href
                    }
                  >
                    <ExternalLinkIcon
                      className="mr-2 size-4"
                      aria-hidden="true"
                    />
                    {t(`${prefix}.detail.openProcessor`)}
                  </Link>
                </Button>
              </div>
            </SectionCard>

            {/* Quem mudou por onde o pagamento sai. É a resposta que o painel
                não tinha: mostrava o estado, nunca quem o produziu. */}
            <ConfigEventsTrail configId={config.id} />
          </div>
        )}

        <ChangeProcessorDialog
          pending={pendingChange}
          onClose={() => setPendingChange(null)}
        />
      </section>
    </DashboardContainer>
  )
}
